import { collection, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";

export type RqProductHeaders = {
  APPRLINIT: string;
  IDSEV: string;
  IDVERSI: string;
  CODCERTIF: string;
  IDPARTN: string;
  VERSI: string;
  VERSIPARN: string;
};

let current: RqProductHeaders | null = null;
let cachedKey = "";
let loadedAt = 0;

export function currentRqProduct(): RqProductHeaders {
  if (!current) {
    throw new Error("No product version is loaded. Open Version in Super Admin and assign a version to this store.");
  }
  return current;
}

export function stampRqHeaders<T extends Record<string, any>>(headers: T): T {
  const product = currentRqProduct();
  return {
    ...headers,
    APPRLINIT: product.APPRLINIT,
    IDSEV: product.IDSEV,
    IDVERSI: product.IDVERSI,
    CODCERTIF: product.CODCERTIF,
    IDPARTN: product.IDPARTN,
    VERSI: product.VERSI,
    VERSIPARN: product.VERSIPARN,
  };
}

type ProductVersionDoc = {
  id: string;
  isDefault?: boolean;
  isActive?: boolean;
  idsev?: unknown;
  idversi?: unknown;
  version?: unknown;
  parentVersion?: unknown;
  certificationStatus?: unknown;
  apprlinit?: unknown;
  codcertif?: unknown;
  idpartn?: unknown;
};

function isPending(docData: ProductVersionDoc): boolean {
  return docData.certificationStatus === "pending";
}

function productFromDoc(docData: ProductVersionDoc): RqProductHeaders {
  const version = String(docData.version || "");
  const parent = isPending(docData) ? String(docData.parentVersion || version) : version;
  return {
    APPRLINIT: String(docData.apprlinit || "SEV"),
    IDSEV: String(docData.idsev || ""),
    IDVERSI: String(docData.idversi || ""),
    CODCERTIF: String(docData.codcertif || ""),
    IDPARTN: String(docData.idpartn || ""),
    VERSI: version,
    VERSIPARN: parent,
  };
}

function certifiedFallback(versions: ProductVersionDoc[]): ProductVersionDoc | undefined {
  const usable = versions.filter((item) => item.isActive !== false);
  const marked = usable.find((item) => item.isDefault);
  if (marked && !isPending(marked)) return marked;
  return usable.find((item) => !isPending(item)) || marked || usable[0];
}

function profileForInstalled(versions: ProductVersionDoc[], installed: string): ProductVersionDoc | undefined {
  const matches = versions.filter((item) => item.isActive !== false && String(item.version || "") === installed);
  return matches.find((item) => !isPending(item)) || matches[0];
}

function stationDocumentId(storeId: string): string {
  try {
    const raw = localStorage.getItem(`workstationOrigin_${storeId}`);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { stationDocumentId?: string };
    return String(parsed?.stationDocumentId || "").trim();
  } catch {
    return "";
  }
}

async function installedAppVersion(): Promise<string> {
  try {
    const api = (window as Window & { api?: { appVersion?: () => Promise<string> } }).api;
    if (!api?.appVersion) return "";
    return String(await api.appVersion() || "").trim();
  } catch {
    return "";
  }
}

export async function ensureRqProduct(storeId?: string): Promise<RqProductHeaders> {
  const id = String(storeId || "").trim();
  const stationId = id ? stationDocumentId(id) : "";
  const cacheKey = `${id}|${stationId}`;
  if (current && cacheKey === cachedKey && Date.now() - loadedAt < 60_000) return current;
  const snap = await getDocs(collection(db, "productVersions"));
  const versions: ProductVersionDoc[] = snap.docs.map((item) => ({
    id: item.id,
    ...(item.data() as Omit<ProductVersionDoc, "id">),
  }));
  const usable = versions.filter((item) => item.isActive !== false);
  let chosen = certifiedFallback(usable);
  let stationRef: ReturnType<typeof doc> | null = null;
  let stationAssigned = "";
  if (id && stationId) {
    stationRef = doc(db, "stores", id, "stations", stationId);
  }
  if (id) {
    const storeSnap = await getDoc(doc(db, "stores", id));
    const storeAssigned = String(storeSnap.get("productVersionId") || "");
    const storeProfile = usable.find((item) => item.id === storeAssigned);
    if (storeProfile) chosen = storeProfile;
    if (stationRef) {
      const stationSnap = await getDoc(stationRef);
      if (stationSnap.exists()) {
        stationAssigned = String(stationSnap.get("productVersionId") || "");
        const stationProfile = usable.find((item) => item.id === stationAssigned);
        if (stationProfile) chosen = stationProfile;
        const installed = await installedAppVersion();
        const matched = installed ? profileForInstalled(usable, installed) : undefined;
        if (matched) chosen = matched;
        const patch: { installedVersion?: string; productVersionId?: string } = {};
        if (installed && installed !== String(stationSnap.get("installedVersion") || "")) patch.installedVersion = installed;
        if (matched && matched.id !== stationAssigned) patch.productVersionId = matched.id;
        if (Object.keys(patch).length) {
          try {
            await updateDoc(stationRef, patch);
          } catch (error) {
            console.error("[rqProduct] could not record the installed version", error);
          }
        }
      }
    }
  }
  if (!chosen || !String(chosen.idsev || "").trim() || !String(chosen.idversi || "").trim()) {
    current = null;
    throw new Error("No product version is configured. Add one under Version in Super Admin.");
  }
  current = productFromDoc(chosen);
  cachedKey = cacheKey;
  loadedAt = Date.now();
  return current;
}
